/**
 * AssetToolPalette — Iter 178.4.
 *
 * The floating orange pill was moved off the drawing (Iter 178.2). This
 * pass promotes the tool selector into the LEFT RAIL alongside Pin so
 * it stops colliding with the SheetStatusBar counts and the
 * OperationalStatusSpine at the bottom-left. The palette is now a pure
 * rail-anchored popover:
 *
 *   • Open state is controlled by the parent (rail button toggles it).
 *   • Panel positions to the RIGHT of the rail, vertically aligned to
 *     the rail's "Asset" tool button.
 *   • Placement flow reuses the banner + drawing tap loop from
 *     SheetAssetMarkers — nothing about type-picking changed.
 */
import React, { useEffect, useRef } from "react";
import { ASSET_TYPES } from "@/components/SheetAssetMarkers";

const RAIL_WIDTH = 76;
const PANEL_GAP  = 8;

export default function AssetToolPalette({
  open,
  onClose,
  placementType,
  setPlacementType,
  anchorTestId = "tool-asset",
  testidPrefix = "sheet-asset-tool",
}) {
  const wrapRef = useRef(null);
  // Iter 178.4 — dismiss on outside pointer / Escape so the palette
  // behaves like every other popover in the app.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        // Ignore taps on the rail button itself — parent handles toggle.
        const anchor = document.querySelector(`[data-testid="${anchorTestId}"]`);
        if (anchor && anchor.contains(e.target)) return;
        onClose?.();
      }
    };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorTestId]);

  if (!open) return null;
  // Suppress the palette while an operator is already dragging an
  // asset onto the sheet — the placement banner owns cancel.
  if (placementType) return null;

  // Vertically align to the rail button. Falls back to a safe top offset
  // if the anchor isn't in the DOM yet.
  let top = 200;
  if (typeof window !== "undefined") {
    const anchor = document.querySelector(`[data-testid="${anchorTestId}"]`);
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      top = Math.round(r.top);
    }
  }

  return (
    <div
      ref={wrapRef}
      className="fixed z-40 rounded-lg border border-[var(--fop-border)] shadow-2xl w-[240px] max-h-[70vh] overflow-y-auto"
      data-testid={`${testidPrefix}-list`}
      style={{
        left: RAIL_WIDTH + PANEL_GAP,
        top,
        background: "linear-gradient(180deg, var(--fop-surface) 0%, var(--fop-surface-2) 100%)",
        boxShadow: "0 14px 42px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      <div className="px-3 pt-2.5 pb-2 border-b border-[var(--fop-border)]">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]">
          Place asset
        </div>
        <div className="text-[13px] text-[var(--fop-text)] font-semibold mt-0.5">
          Pick a type
        </div>
      </div>
      <div className="p-1.5">
        {ASSET_TYPES.map(([type, label]) => (
          <button
            key={type}
            type="button"
            onClick={() => { setPlacementType(type); onClose?.(); }}
            data-testid={`${testidPrefix}-pick-${type}`}
            className="group w-full text-left flex items-center justify-between gap-2 px-2.5 py-2 rounded-md text-[13px] text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)] hover:text-white transition-colors"
            style={{ minHeight: 42 }}
          >
            <span className="font-medium truncate">{label}</span>
            <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] group-hover:text-[var(--fop-primary)] transition-colors shrink-0">
              {type}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
